import type { ReactNode } from 'react';

export type StatusTone = 'ok' | 'bad' | 'warn' | 'off' | 'info';

/** Known statuses across the schema (jobs, runs, checks, connections, opportunities). */
const KNOWN: Record<string, { tone: StatusTone; label: string }> = {
  ok: { tone: 'ok', label: 'OK' },
  done: { tone: 'ok', label: 'Done' },
  connected: { tone: 'ok', label: 'Connected' },
  worked: { tone: 'ok', label: 'Worked' },
  active: { tone: 'ok', label: 'Active' },
  failed: { tone: 'bad', label: 'Failed' },
  error: { tone: 'bad', label: 'Error' },
  refused: { tone: 'bad', label: 'Refused' },
  expired: { tone: 'warn', label: 'Expired' },
  pending: { tone: 'warn', label: 'Pending' },
  queued: { tone: 'info', label: 'Queued' },
  running: { tone: 'info', label: 'Running' },
  measuring: { tone: 'info', label: 'Measuring' },
  in_progress: { tone: 'info', label: 'In progress' },
  assigned: { tone: 'info', label: 'Assigned' },
  open: { tone: 'warn', label: 'Open' },
  skipped: { tone: 'off', label: 'Skipped' },
  cached: { tone: 'off', label: 'Cached' },
  disconnected: { tone: 'off', label: 'Not connected' },
  dismissed: { tone: 'off', label: 'Dismissed' },
  snoozed: { tone: 'off', label: 'Snoozed' },
  no_effect: { tone: 'off', label: 'No effect' },
  paused: { tone: 'off', label: 'Paused' },
};

export function statusInfo(status: string | null | undefined): { tone: StatusTone; label: string } {
  if (!status) return { tone: 'off', label: 'Unknown' };
  const k = KNOWN[status];
  if (k) return k;
  const label = status.replace(/_/g, ' ');
  return { tone: 'off', label: label.charAt(0).toUpperCase() + label.slice(1) };
}

/**
 * The reference status pill. Pass a schema status (`ok`, `failed`, `expired`…) for the standard
 * tone and label, or a `tone` and `label`/children of your own.
 */
export function StatusPill({
  status,
  tone,
  label,
  children,
  title,
}: {
  status?: string | null;
  tone?: StatusTone;
  label?: string;
  children?: ReactNode;
  title?: string;
}) {
  const info = statusInfo(status);
  const t = tone ?? info.tone;
  return (
    <span className={`status st-${t}`} title={title}>
      {children ?? label ?? info.label}
    </span>
  );
}
